import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FinanceRoutes } from '../../../finance/finance-route-lookups';
import { CartService } from '../../../finance/shared/services/cart.service';
import { ISourceSystemType, SourceSystemType } from '../../../foundation/models/enums/source-system-type.enum';
import { appInjector } from '../../app-injector';
import { RouteParameterType } from '../../enums/route-parameters.enum';
import { RoutePartType } from '../../enums/route-part.enum';
import { IRoute } from '../../models/iroute.interface';
import { AuxRoute } from '../../models/route/aux-route.model';
import { RouteConfiguration } from '../../models/route/route-configuration.model';
import { RouteDefaults } from '../../models/route/route-defaults.model';
import { TemplateConfiguration } from '../../models/template/template-configuration.model';
import { sourceSystemDefaultRedirectLookup } from '../../source-system-default-redirect-lookup';
import { NavigationService } from '../navigation/navigation.service';
import { IRouteBuilder } from './route-builder.interface';

@Injectable()
/**
 * Builds transaction routes
 */
export abstract class RouteBuilderService implements IRouteBuilder {

    private _navigationService: NavigationService;
    private _cartService: CartService;

    protected financeRoutes: any;

    protected sourceSystemTypes: SourceSystemType = new SourceSystemType();

    constructor() {
        this.financeRoutes = FinanceRoutes;
    }

    /**
     * Gets the home navigation route
     * @returns An the home route
     */
    public getHomeRoute(pageTitle?: string): IRoute {
        return { routeName: '/', pageTitle: pageTitle };
    }

    /**
     * Gets the route list for the transaction type provided.
     * @returns: Array of routes for transaction.
     */
    public abstract getRouteList(transactionTypeId: number): any[];

    /**
     * Gets the navigation service
     */
    public get navigationService(): NavigationService {
        if (this._navigationService == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._navigationService = <NavigationService> injector.get(NavigationService);
            }
        }

        return this._navigationService;
    }

    /**
     * Gets the cart service
     */
    public get cartService(): CartService {
        if (this._cartService == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._cartService = <CartService> injector.get(CartService);
            }
        }

        return this._cartService;
    }

    /**
     * Returns the source system type for the route builder
     */
    public abstract get sourceSystemType(): ISourceSystemType;

    /**
     * Returns the root of the route, i.e. the source system/area entrance
     */
    public getRouteRoot(routeParameters: any): string {
        return sourceSystemDefaultRedirectLookup[this.sourceSystemType.id];
    }

    /**
     * Gets the collection of auxillary route parts to fully populate the specified source system's transaction template
     */
    public getTransactionAuxillaryRoutes(templateConfiguration: TemplateConfiguration, routeConfiguration: RouteConfiguration, defaults: RouteDefaults, routeParameters: any) {
        const auxRoutes: any[] = [];

        const templateDef = templateConfiguration.templateParts;
        if (templateDef != undefined) {
            templateDef.forEach(function(templatePart, index, array) {
                if (templatePart.name != RoutePartType.primary) {
                    // get the corresponding routeConfiguration
                    const auxRouteConfiguration = routeConfiguration.routes.find(r => r.outletName == templatePart.name);

                    // build only aux routes, not the primary
                    const auxRoute = new AuxRoute();
                    auxRoute.outletName = templatePart.name;
                    auxRoute.isModule = auxRouteConfiguration != undefined ? auxRouteConfiguration.isModule : true;
                    // comment this
                    auxRoute.routeName = templatePart.isDefaultNavigationTarget && defaults != undefined && defaults.routeName != undefined && defaults.routeName.length > 0
                        ? defaults.routeName
                        : (auxRouteConfiguration != undefined ? auxRouteConfiguration.routeName : templatePart.defaultRouteName);

                    auxRoute.routeParameter = [];
                    if (auxRouteConfiguration != undefined && auxRouteConfiguration.routeParameter != undefined) {
                        auxRouteConfiguration.routeParameter.forEach((paramValue, paramInex, paramArray) => {
                            if (routeParameters != undefined && routeParameters[paramValue] != undefined) {
                                auxRoute.routeParameter.push(routeParameters[paramValue]);
                            }
                        });
                    }

                    auxRoutes.push(auxRoute);
                }
            });
        }

        return auxRoutes;
    }

    /**
     * Gets an auxiliary route
     */
    public getAuxRoute(routeName: string, routeParameters: any[]): AuxRoute {
        const retVal = new AuxRoute();
        retVal.routeName = routeName;
        retVal.routeParameter = routeParameters;
        retVal.isModule = true;
        return retVal;
    }

    /**
     * Builds the auxillary route command for navigation from the auxillary route parts
     */
    public buildTransactionRoutePart(templateConfiguration: TemplateConfiguration, routeConfiguration: RouteConfiguration, defaults: RouteDefaults, routeParameters: any) {
        return this.buildAuxiliaryRouteCommand(this.getTransactionAuxillaryRoutes(templateConfiguration, routeConfiguration, defaults, routeParameters));
    }

    /**
     * Builds up the object used for navigating to auxiliary routes.
     * @param args: Array of AuxRoute item that contain details for the auxiliary routes.
     * @returns: Populated object that is used by the router.
     */
    public buildAuxiliaryRouteCommand(args: AuxRoute[]) {
        const command = { outlets: {} };
        for (var i = 0; i < args.length; i++) {
            const route: AuxRoute = args[i];

            command.outlets[route.outletName] = [];
            command.outlets[route.outletName].push(route.routeName);

            if (route.isModule) {
                const moduleOutlet = { outlets: {} };
                if (route.routeParameter && route.routeParameter.length > 0) {
                    if (route.outletName === 'body' || route.outletName === 'head' || route.outletName === 'sidebar') {
                        moduleOutlet.outlets['primary'] = ['aux', ...route.routeParameter];
                    } else {
                        moduleOutlet.outlets['primary'] = route.routeParameter;
                    }
                } else {
                    moduleOutlet.outlets[route.outletName] = [];
                }

                command.outlets[route.outletName].push(moduleOutlet);
            } else {
                command.outlets[route.outletName].push(route.routeParameter);
            }
        }

        return command;
    }

    public rebuildUrlRouterOutlets(fullRoute: ActivatedRoute, replaceOutletName: string, replaceOutletDefinition: any) {
        const command = { outlets: {} };
        let route = fullRoute;

        // first go up the tree until you can get all of the children
        while (route.parent != undefined && route.outlet != RoutePartType.primary) {
            route = route.parent;
        }
        const outlets = route.children;

        if (outlets != undefined && outlets.length > 0) {
            command.outlets = this.parseOutlets(outlets, replaceOutletName, replaceOutletDefinition);
        }
        return command;
    }

    private parseOutlets(activatedRoutes: ActivatedRoute[], replaceOutletName: string, replaceOutletDefinition: any) {
        const arr = {};
        const self = this;

        activatedRoutes.forEach(function(value: ActivatedRoute, index: number, array: ActivatedRoute[]) {
            if (value.outlet == replaceOutletName) {
                arr[value.outlet] = replaceOutletDefinition;
            } else {
                arr[value.outlet] = [];
                const url: any = value.url;
                for (let i = 0; i < url.length; i++) {
                    arr[value.outlet].push(value.url[i].path);
                }
                if (value.children != undefined && value.children.length > 0) {
                    arr[value.outlet].push(self.parseOutlets(value.children, replaceOutletName, replaceOutletDefinition));
                }
            }
        });

        return arr;
    }

    /**
     * Gets the full absolute path to the source system/transaction
     */
    public getAbsoluteRoute(templateConfiguration: TemplateConfiguration, routeConfiguration: RouteConfiguration, defaults: RouteDefaults, routeParameters: any) {

        let navigationParts: any[];
        navigationParts = [];

        if (routeConfiguration.isNonAuxRoute) {
            const root = this.getRouteRoot(routeParameters);
            const parameter = routeParameters[RouteParameterType.id];
            return ['/', root, 'summary', parameter];
        }

        navigationParts.push(this.getRouteRoot(routeParameters));

        // check for parameters at the root
        const routes = routeConfiguration.routes;
        if (routes != undefined) {
            const primary = routes.find(t => t.outletName == RoutePartType.primary);
            if (primary != undefined && primary.routeParameter != undefined && primary.routeParameter.length > 0) {
                primary.routeParameter.forEach(function(parameter, parameterIndex, parameterArray) {
                    if (routeParameters[parameter] != undefined) {
                        navigationParts.push(routeParameters[parameter]);
                    } else if (parameter == RouteParameterType.templateConfigurationName) {
                        navigationParts.push(templateConfiguration.name);
                    } else if (parameter == RouteParameterType.routeConfigurationName) {
                        navigationParts.push(routeConfiguration.name);
                    }
                });
            }
        }

        navigationParts.push(this.buildTransactionRoutePart(templateConfiguration, routeConfiguration, defaults, routeParameters));

        return navigationParts;
    }

}
